import { ButtonCus } from '@/components/common/Button';
import TableActionButtons from '@/components/common/TableActionButtons';
import Table from '@/components/table/Table';
import {
    COUPON_PATH,
    COUPON_TYPES,
    DATE_YMD_2,
    DEFAULT_SELECT_PAGE_SIZES,
    ROUTES
} from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { CouponModel } from '@/types/coupon';
import { COUPON_TYPE } from '@/types/enums';
import { formatDateTime } from '@/utils/datetime';
import { showDiscount } from '@/utils/utils';
import { formatNumber } from '@/utils/formatNumber';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import { CirclePlus } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

export default observer(function ForeverCouponList() {
    // hook
    const { t } = useTranslation();
    const navigate = useNavigate();

    // store
    const {
        couponStore: {
            foreverCoupons,
            pagingForever,
            totalRecordForever,
            getCouponsCMS
        }
    } = useStore();

    // columns
    const columns: ColumnDef<CouponModel>[] = [
        {
            id: 'id',
            accessorKey: 'id',
            header: '',
            size: 80,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <TableActionButtons onView={() => handleClickEditCoupon(original.id)} />
            )
        },
        {
            accessorKey: 'couponType',
            header: t('field.coupon_type'),
            size: 120,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>{t(`const.${COUPON_TYPES[original.couponType].key}`)}</div>
            )
        },
        {
            accessorKey: 'name',
            header: t('field.name_of_coupon'),
            size: 450,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='line-clamp-5'>{original.name}</div>
            )
        },
        {
            accessorKey: 'code',
            header: t('field.code_of_coupon'),
            size: 450,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='line-clamp-5'>{original.code}</div>
            )
        },
        {
            accessorKey: 'couponQuantity',
            header: `${t('field.quantity_used')}/${t('field.release_quantity')}`,
            size: 350,
            enableSorting: false,
            meta: {
                headerClassName: 'right-sub-header-item',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div className='text-right'>
                    {
                        `${original.usedNumber ? formatNumber(original.usedNumber) : '0'
                        }/${original.couponQuantity ?
                            formatNumber(original.couponQuantity) :
                            '0'
                        }`
                    }
                </div>
            )
        },
        {
            accessorKey: 'discountRate',
            header: t('field.discount'),
            size: 350,
            enableSorting: false,
            meta: {
                headerClassName: 'right-sub-header-item',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => <div className='text-right'>{showDiscount(original.promotionType, original.discountRate)}</div>
        },
        {
            accessorKey: 'startDate',
            header: t('field.start_date'),
            size: 400,
            enableSorting: true,
            cell: ({ row: { original } }) => (
                <div>{formatDateTime(original.startDate, DATE_YMD_2)}</div>
            )
        }
    ];

    // lifecycle
    useEffect(() => {
        onFetchCoupon({ couponType: COUPON_TYPE.FOREVER_USE }, pagingForever);

        return () => {
            // clean();
        };
    }, []);

    // function
    const onFetchCoupon = (searchParams?: any, paging?: TableState) => {
        getCouponsCMS(searchParams, paging);
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging =
            stateUpdater instanceof Function ?
                stateUpdater(pagingForever) :
                stateUpdater;
        onFetchCoupon({ couponType: COUPON_TYPE.FOREVER_USE }, newPaging);
    };

    const handleClickEditCoupon = (id: number) => {
        navigate(`${ROUTES.couponEdit.href}/${COUPON_PATH.FOREVER}/${id}`);
    };

    return (
        <div className='layout-card'>
            <Table
                columns={columns}
                data={foreverCoupons}
                state={pagingForever}
                rowCount={totalRecordForever}
                onStateChange={onStateChange}
                selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                getRowId={row => row.id?.toString()}
                renderActions={(
                    <Link to={`${ROUTES.locationCreate.href}/${COUPON_PATH.FOREVER}`}>
                        <ButtonCus variant='default' size='md'>
                            <CirclePlus className='mr-2' />
                            {t('button.add_new')}
                        </ButtonCus>
                    </Link>
                )}
            />
        </div>
    );
});
